import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { TrendingUp, Search, Zap, FileText, AlertCircle, Clock, BarChart3, Settings } from 'lucide-react';
import SiteHealthMetrics from './components/SiteHealthMetrics';
import CoreWebVitalsPanel from './components/CoreWebVitalsPanel';
import KeywordPerformanceTable from './components/KeywordPerformanceTable';
import MetaTagManager from './components/MetaTagManager';
import TechnicalAuditPanel from './components/TechnicalAuditPanel';
import GoogleAnalyticsMetrics from './components/GoogleAnalyticsMetrics';
import Icon from '../../components/AppIcon';


const TechnicalSEOPerformanceAnalyzer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [seoData, setSeoData] = useState({
    siteHealth: {
      overallScore: 87,
      indexedPages: 42,
      crawlErrors: 3,
      brokenLinks: 5,
      duplicateContent: 2
    },
    coreWebVitals: {
      lcp: { value: 2.1, status: 'good' },
      fid: { value: 85, status: 'good' },
      cls: { value: 0.08, status: 'good' },
      ttfb: { value: 0.6, status: 'good' }
    },
    keywords: [
      { keyword: 'envoi colis côte d\'ivoire', position: 3, volume: 1200, difficulty: 45, trend: 'up' },
      { keyword: 'transport maritime abidjan', position: 7, volume: 890, difficulty: 52, trend: 'stable' },
      { keyword: 'groupage france afrique', position: 12, volume: 650, difficulty: 38, trend: 'up' },
      { keyword: 'conteneur vers abidjan', position: 15, volume: 420, difficulty: 48, trend: 'down' },
      { keyword: 'expédition colis international', position: 8, volume: 980, difficulty: 55, trend: 'up' }
    ],
    pages: [
      { url: '/', title: 'Accueil - Suivi Colis', metaDescription: 'Suivez vos colis en temps réel', status: 'optimized' },
      { url: '/shipping-information', title: 'Informations Expédition', metaDescription: 'Tout sur nos services d\'expédition', status: 'needs-improvement' },
      { url: '/tracking/:reference', title: 'Suivi Envoi', metaDescription: '', status: 'missing-meta' }
    ],
    technicalIssues: [
      { type: 'error', severity: 'high', issue: '3 pages avec erreur 404', affected: 3, priority: 'urgent' },
      { type: 'warning', severity: 'medium', issue: '5 liens cassés détectés', affected: 5, priority: 'high' },
      { type: 'info', severity: 'low', issue: '2 contenus dupliqués', affected: 2, priority: 'medium' }
    ]
  });

  useEffect(() => {
    loadSEOData();
  }, []);

  const loadSEOData = async () => {
    try {
      setLoading(true);
      // Simulate API call - in production, fetch from SEO service
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoading(false);
    } catch (error) {
      console.error('Error loading SEO data:', error);
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'keywords', label: 'Mots-clés', icon: Search },
    { id: 'performance', label: 'Performance', icon: Zap },
    { id: 'meta-tags', label: 'Meta Tags', icon: FileText },
    { id: 'technical', label: 'Audit Technique', icon: Settings },
    { id: 'analytics', label: 'Google Analytics', icon: TrendingUp }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement des données SEO...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingUp className="text-green-600" size={32} />
                Analyseur SEO & Performance
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Optimisation technique et suivi des performances de référencement
              </p>
            </div>
            <button
              onClick={loadSEOData}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Clock size={18} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap border-b-2 ${
                    activeTab === tab?.id
                      ? 'border-green-600 text-green-600 bg-green-50' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  {tab?.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              <SiteHealthMetrics data={seoData?.siteHealth} />
              <CoreWebVitalsPanel vitals={seoData?.coreWebVitals} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="text-orange-600" size={20} />
                    Problèmes Prioritaires
                  </h3>
                  <div className="space-y-3">
                    {seoData?.technicalIssues?.map((issue, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${
                          issue?.severity === 'high' ? 'bg-red-100 text-red-600' :
                          issue?.severity === 'medium'? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          <AlertCircle size={16} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{issue?.issue}</p>
                          <p className="text-sm text-gray-600">Priorité: {issue?.priority}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Search className="text-green-600" size={20} />
                    Top Mots-clés
                  </h3>
                  <div className="space-y-3">
                    {seoData?.keywords?.slice(0, 5)?.map((kw, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{kw?.keyword}</p>
                          <p className="text-sm text-gray-600">Volume: {kw?.volume}/mois</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">#{kw?.position}</p>
                          <p className="text-xs text-gray-500">Position</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'keywords' && (
            <KeywordPerformanceTable keywords={seoData?.keywords} />
          )}

          {activeTab === 'performance' && (
            <CoreWebVitalsPanel vitals={seoData?.coreWebVitals} detailed={true} />
          )}

          {activeTab === 'meta-tags' && (
            <MetaTagManager pages={seoData?.pages} />
          )}

          {activeTab === 'technical' && (
            <TechnicalAuditPanel issues={seoData?.technicalIssues} siteHealth={seoData?.siteHealth} />
          )}

          {activeTab === 'analytics' && (
            <GoogleAnalyticsMetrics />
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicalSEOPerformanceAnalyzer;